import { NgFor } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import { VatPercentages } from 'src/app/shared/constants';

import { Cycle, FinalSetup } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { AdditionalInvoiceDataRow } from '../final-setup/models/final-setup.model';

export enum EditAdditionalInvoiceMode {
  Add = 'add',
  Edit = 'edit',
}

export interface FinalSetupEditAdditionalInvoiceDialogInputData extends AbstractDialogData {
  additionalInvoiceRow?: AdditionalInvoiceDataRow;
  cycleId: number;
  mode: EditAdditionalInvoiceMode;
}

@Component({
  selector: 'app-final-setup-edit-additional-invoice.component',
  templateUrl: 'final-setup-edit-additional-invoice.component.html',
  imports: [
    DialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AgrNumberDirective,
    MatSelect,
    MatOption,
    NgFor,
    TranslatePipe,
  ],
})
export class FinalSetupEditAdditionalInvoiceComponent extends AbstractDialog implements OnInit {
  data = inject<FinalSetupEditAdditionalInvoiceDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<FinalSetupEditAdditionalInvoiceComponent, AbstractDialogResultData>>(
      MatDialogRef,
    );
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<RootState>>(Store);

  id: number;
  vatPercentages = VatPercentages;
  additionalInvoiceRow = this.data.additionalInvoiceRow;

  formGroup = new FormGroup({
    invoiceDate: new FormControl<Date>(null, []),
    invoiceNumber: new FormControl<string>(null, []),
    amount: new FormControl<number>(null, [Validators.required]),
    vatPercentage: new FormControl<number>(null, []),
    description: new FormControl<string>(null, []),
  });

  private readonly selectedCycle$: Observable<Cycle> = this.store.select(getSelectedCycle);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.data.title = this.localeService.translate(this.data.mode);
  }

  ngOnInit() {
    if (this.data.mode === EditAdditionalInvoiceMode.Edit) {
      this.formGroup.controls.invoiceDate.setValue(this.additionalInvoiceRow.invoiceDate);
      this.formGroup.controls.invoiceNumber.setValue(this.additionalInvoiceRow.invoiceNumber);
      this.formGroup.controls.amount.setValue(this.additionalInvoiceRow.amount);
      this.formGroup.controls.vatPercentage.setValue(this.additionalInvoiceRow.vatPercentage);
      this.formGroup.controls.description.setValue(this.additionalInvoiceRow.description);
    }
  }

  save() {
    switch (this.data.mode) {
      case EditAdditionalInvoiceMode.Add:
        this.saveAddData();
        break;
      case EditAdditionalInvoiceMode.Edit:
        this.saveEditData();
        break;
    }
  }

  private saveEditData() {
    if (this.formGroup.valid) {
      this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
        const finalSetup = this.getOrCreateCycleFinalSetup(selectedCycle);
        const invoices = finalSetup.additionalInvoices.filter(
          (i) => i.id !== this.additionalInvoiceRow.id,
        );
        this.upsertCycleFinalSetup(selectedCycle, {
          ...finalSetup,
          additionalInvoices: [
            ...invoices,
            {
              id: this.additionalInvoiceRow.id,
              amount: this.formGroup.controls.amount.value,
              description: this.formGroup.controls.description.value,
              invoiceDate: this.formGroup.controls.invoiceDate.value,
              invoiceNumber: this.formGroup.controls.invoiceNumber.value,
              vatPercentage: this.formGroup.controls.vatPercentage.value,
            },
          ],
        });
      });

      super.confirm();
    }
  }

  private saveAddData() {
    if (this.formGroup.valid) {
      this.selectedCycle$.pipe(take(1)).subscribe((selectedCycle) => {
        const finalSetup = this.getOrCreateCycleFinalSetup(selectedCycle);
        this.upsertCycleFinalSetup(selectedCycle, {
          ...finalSetup,
          additionalInvoices: [
            ...finalSetup.additionalInvoices,
            {
              id: this.getNextId(finalSetup.additionalInvoices),
              amount: this.formGroup.controls.amount.value,
              description: this.formGroup.controls.description.value,
              invoiceDate: this.formGroup.controls.invoiceDate.value,
              invoiceNumber: this.formGroup.controls.invoiceNumber.value,
              vatPercentage: this.formGroup.controls.vatPercentage.value,
            },
          ],
        });
      });

      super.confirm();
    }
  }

  private getOrCreateCycleFinalSetup(selectedCycle: Cycle): FinalSetup {
    const finalSetup: FinalSetup = selectedCycle.finalSetup ?? {};
    return {
      ...finalSetup,
      additionalInvoices: finalSetup?.additionalInvoices ?? [],
    };
  }

  private getNextId(additionalInvoices: AdditionalInvoiceDataRow[]): number {
    return additionalInvoices.length > 0
      ? Math.max(...additionalInvoices.map((row: AdditionalInvoiceDataRow) => row.id)) + 1
      : 1;
  }

  private upsertCycleFinalSetup(selectedCycle: Cycle, finalSetup: FinalSetup) {
    this.store.dispatch(
      cycleActions.upsertCycleGeneral({
        cycle: {
          ...selectedCycle,
          finalSetup,
        },
      }),
    );
  }
}
